// A set of layered sections of content — known as tab panels — that are displayed one at a time
// https://www.radix-ui.com/primitives/docs/components/tabs
'use client';

import React from 'react';
import * as RadixTabs from '@radix-ui/react-tabs';
import cn from 'classnames';
import { useDirection } from './useDirection';
import styles from './tabs.module.sass';

type Tab = {
    title: string;
    content: React.ReactNode;
    /**
     * When true, prevents the user from interacting with the tab
     */
    disabled?: boolean;
    dataTestId?: string;
};

interface TabsProps {
    tabs: Tab[];
    onValueChange?: (value: string) => void;
    /**
     * @param value `value` prop of the tab trigger
     */
    value?: string;
    /**
     * The value of the tab that should be active when initially rendered. Use when you do not need to control the
     * state of the tabs.
     * @example `tab-0` is the first tab, `tab-1` is the second tab, etc.
     */
    defaultValue?: string;
    /**
     * Instead of rendering triggers with tab titles at the top, text triggers are replaced with dots and rendered
     * under the tabs
     */
    withDotTriggers?: boolean;
    switchers?: boolean;
    style?: React.CSSProperties;
    /** keep tab panels mounted to avoid remounting on switch (helps Safari) */
    keepMounted?: boolean;
}

/**
 * A set of layered sections of content — known as tab panels — that are displayed one at a time.
 */
export const Tabs = ({
    tabs = [],
    value,
    defaultValue = 'tab-0',
    onValueChange,
    withDotTriggers,
    switchers,
    style,
    keepMounted = false,
}: TabsProps) => {
    const direction = useDirection();

    const triggers = (
        <RadixTabs.List
            className={cn(styles.TabsList, switchers && styles.switchers)}
            aria-label="Tabs"
            data-test-id="tab-list"
        >
            {tabs.map((t, i) => {
                const activeTabIndex = value ? parseInt(value.split('-')[1], 10) : 0;

                return (
                    <RadixTabs.Trigger
                        disabled={t.disabled}
                        className={cn(
                            styles.Trigger,
                            withDotTriggers ? styles.DotTrigger : styles.TextTrigger,
                            // styles indicating that steps in tabs preceding active tab are completed,
                            // e.g. grayed out bg
                            activeTabIndex > i && styles.Done
                        )}
                        value={`tab-${i}`}
                        key={`tab-title-${i}`}
                        data-test-id={t?.dataTestId || `tab-${i}`}
                    >
                        {!withDotTriggers && t.title}
                    </RadixTabs.Trigger>
                );
            })}
            {switchers && <em className={styles.bar}></em>}
        </RadixTabs.List>
    );

    return (
        <RadixTabs.Root
            className={styles.TabsRoot}
            defaultValue={defaultValue}
            onValueChange={onValueChange}
            dir={direction}
            value={value}
        >
            {!withDotTriggers && triggers}
            {tabs.map((t, i) => (
                <RadixTabs.Content
                    className={styles.TabsContent}
                    value={`tab-${i}`}
                    key={`tab-content-${i}`}
                    style={style}
                    {...(keepMounted && { forceMount: true })}
                >
                    {t.content}
                </RadixTabs.Content>
            ))}
            {withDotTriggers && triggers}
        </RadixTabs.Root>
    );
};
